import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWitnesses } from './witnessesSlice';
import { RootState } from 'app/store/rootReducer';
import { Col, Row, Input, InputGroup, InputGroupText, Card, CardBody, Button } from 'reactstrap';
import WitnessesList from './witnessesList/WitnessesList';
import { useLocation, useNavigate } from 'react-router-dom';
import useModal from 'helpers/useModal';
import { useAuth } from 'helpers/authContext';
import { ConfirmWitnessVoteModal } from './witnessModals/ConfirmWitnessVoteModal';
import Skeleton from 'react-loading-skeleton';
import { LoginModal } from 'features/user/userModals/LoginModal';
import { SignupModal } from 'features/user/userModals/SignupModal';

export const WitnessesMainPage = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    
    const { witnesses, isLoading, error } = useSelector((state: RootState) => state.witnesses);
    const [searchQuery, setSearchQuery] = useState("");
    
    // For Deep Links
    const { isOpenWitnessVoteModal, toggleWitnessVoteModal } = useModal();
    const { isOpenLoginModal, toggleLoginModal, isOpenSignupModal, toggleSignupModal } = useModal();
    const [targetWitness, setTargetWitness] = useState("");
    
    const { user } = useAuth();
    const [userWitnessVotes, setUserWitnessVotes] = useState<string[]>([]);

    const loadUserVotes = () => {
        if (user) {
            import("services/dsteem-service").then(module => {
                module.getAccount(user).then((acc: any) => {
                    if (acc && acc.length > 0) {
                        setUserWitnessVotes(acc[0].witness_votes || []);
                    }
                }).catch(err => {
                    console.error("Error loading user votes:", err);
                });
            }).catch(err => {
                console.error("Error importing dsteem-service:", err);
            });
        }
    };

    useEffect(() => {
        loadUserVotes();
    }, [user]);

    const loadWitnesses = () => {
        dispatch(fetchWitnesses() as any);
    };

    const handleVoteSuccess = () => {
        loadUserVotes(); // Refresh only user votes for instant feedback
        // Optionally loadWitnesses() if we want to refresh global stats
    };

    useEffect(() => {
        loadWitnesses();
    }, [dispatch]);

    // Handle URL parameters for deep linking
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const op = queryParams.get("op");
        const id = queryParams.get("id");

        if (op === "voteWitness" && id) {
            if (!user) {
                if (!isOpenLoginModal && !isOpenSignupModal) toggleLoginModal();
                return;
            }

            setTargetWitness(id);
            if (!isOpenWitnessVoteModal) {
                toggleWitnessVoteModal();
            }
            
            // Clean up URL once the modal is open or handled
            navigate(location.pathname, { replace: true });
        }
    }, [location.search, navigate, isOpenWitnessVoteModal, toggleWitnessVoteModal, user]);

    return (
        <div className="p-3 pt-0">

                {/* Main Content */}
                <Row>
                    <Col xl="12">
                        <div className="w-100">
                            <div className="d-flex justify-content-between align-items-end mb-4 flex-wrap">
                                <div>
                                    <h1 className="mb-0" style={{ color: 'var(--letters)' }}>Steem Witnesses</h1>
                                    {user && (
                                        <div className="mt-1 d-flex align-items-center">
                                            <span className="text-muted mr-2" style={{ fontSize: '0.9rem' }}>Votes used:</span>
                                            <span className="font-weight-bold" style={{ color: 'var(--speem)', fontSize: '0.9rem' }}>
                                                {userWitnessVotes.length} / 30
                                            </span>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="mt-3 mt-md-0" style={{ width: '100%', maxWidth: '300px' }}>
                                    <InputGroup className="input-group-alternative fancy-input shadow-sm">
                                        <InputGroupText className="bg-transparent border-0 pr-0 pl-4">
                                            <i className="fas fa-search text-muted" />
                                        </InputGroupText>
                                        <Input 
                                            className="border-0 shadow-none bg-transparent"
                                            placeholder="Search witness..." 
                                            type="text" 
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            style={{ color: 'var(--letters)' }}
                                        />
                                    </InputGroup>
                                </div>
                            </div>

                            {error ? (
                                <div className="text-center p-5 card shadow-none border" style={{ backgroundColor: 'var(--bg-light)', borderRadius: '15px' }}>
                                    <div className="mb-3">
                                        <i className="fas fa-wifi text-warning" style={{ fontSize: '3rem' }} />
                                    </div>
                                    <h3 className="mb-2" style={{ color: 'var(--letters)' }}>Connection Issue</h3>
                                    <p className="text-muted mb-4">{error.includes('timeout') ? 'The request took too long to respond. Please check your connection.' : error}</p>
                                    <div className="d-flex justify-content-center">
                                        <Button color="default" className="btn-fancy px-5" onClick={loadWitnesses}>
                                            <i className="fas fa-redo mr-2" />
                                            Try Again
                                        </Button>
                                    </div>
                                </div>
                            ) : isLoading ? (
                                <div className="mt-3">
                                    {[...Array(8)].map((_, i) => (
                                        <Card key={i} className="shadow-none border mb-3" style={{ backgroundColor: 'var(--bg-light)', borderRadius: '15px' }}>
                                            <CardBody className="p-3">
                                                <Row className="align-items-center m-0 flex-nowrap">
                                                    <Col className="col-auto pr-0 d-flex justify-content-start align-items-center" style={{ width: '55px' }}>
                                                        <Skeleton width={30} height={30} />
                                                    </Col>
                                                    <Col className="col" style={{ minWidth: '0' }}>
                                                        <Row className="align-items-center flex-nowrap">
                                                            <Col className="col-auto">
                                                                <Skeleton circle width={40} height={40} />
                                                            </Col>
                                                            <Col className="col pl-0">
                                                                <Skeleton width={150} height={20} />
                                                                <div className="mt-1">
                                                                    <Skeleton width={100} height={15} />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <WitnessesList 
                                    witnesses={witnesses} 
                                    searchQuery={searchQuery} 
                                    reloadWitnesses={handleVoteSuccess}
                                    userWitnessVotes={userWitnessVotes}
                                />
                            )}
                        </div>
                    </Col>
                </Row>
            

            <ConfirmWitnessVoteModal
                isShowing={isOpenWitnessVoteModal}
                hide={toggleWitnessVoteModal}
                witnessName={targetWitness}
                onSuccess={handleVoteSuccess}
            />

            <LoginModal 
                isShowing={isOpenLoginModal}
                hide={toggleLoginModal}
                onSignup={toggleSignupModal}
            />

            <SignupModal
                isShowing={isOpenSignupModal}
                hide={toggleSignupModal}
            />
        </div>
    );
};

export default WitnessesMainPage;
